import { EntityRefUtil, GetChecksResponseItemDTO, getProgressStatus } from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import CheckProgressBar from '@/components/checks/check-progress-bar';
import { ExclamationIcon } from '@/components/icons/exclamation';
import { Badge } from '@/components/ui/badge';
import { getUpcomingChecksDashboardNewQueryOptions } from '@/queries/checks';
import { getWorkspaceRoute } from '@/utils/routes';
import { formatLastReading, formatScheduleCheckLabel } from '@/utils/schedules';

export const ServiceTable = () => {
  const navigate = useNavigate();

  const { data: newChecksData, isLoading } = useQuery({
    ...getUpcomingChecksDashboardNewQueryOptions(),
  });

  return (
    <div className="flex">
      <div className="flex flex-col gap-[20px]">
        <div className="flex flex-col">
          <span className="text-xl leading-[30px] font-medium">Schedules</span>
          <span className="text-secondary-text text-xs leading-[18px] font-normal">Schedule Checks, sorted by Due Progress</span>
        </div>
        <div className="h-[243px] overflow-auto">
          <table className="w-full table-fixed">
            <thead className="bg-grey-1">
              <tr>
                <th className="w-1/4 rounded-l-[5px] p-[10px] text-left text-sm/[21px] font-bold">Name</th>
                <th className="w-1/4 p-[10px] text-left text-sm/[21px] font-bold">Schedule Check</th>
                <th className="w-1/4 p-[10px] text-left text-sm/[21px] font-bold">Last Reading</th>
                <th className="w-1/4 rounded-r-[5px] p-[10px] text-left text-sm/[21px] font-bold">Due Progress</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <LoadingRows />
              ) : (
                <NewChecksRows
                  data={newChecksData?.data ?? []}
                  onRowClick={(checkId) => {
                    if (checkId) {
                      navigate(getWorkspaceRoute('/schedules/checks'), { state: { checkId } });
                    }
                  }}
                />
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function LoadingRows() {
  return (
    <>
      {[1, 2, 3, 4].map((_, i) => (
        <tr key={i}>
          <td width="40%" className="placeholder-glow">
            <span className="placeholder placeholder-lg w-100" />
            <span className="placeholder placeholder-lg w-25" />
          </td>
          <td width="25%" className="placeholder-glow align-top">
            <span className="placeholder placeholder-lg w-100" />
          </td>
          <td width="20%" className="placeholder-glow align-top">
            <span className="placeholder placeholder-lg w-100" />
          </td>
          <td width="15%" className="placeholder-glow align-top">
            <span className="placeholder placeholder-lg w-100" />
          </td>
        </tr>
      ))}
    </>
  );
}

function NewChecksRows({ data, onRowClick }: { data: GetChecksResponseItemDTO[]; onRowClick: (checkId: string | null) => void }) {
  return (
    <>
      {data.map((check, i) => {
        const dueProgress = check.dueProgress ?? 0;
        const status = getProgressStatus(dueProgress, check.dateRemaining);
        const scheduleCheckLabel = formatScheduleCheckLabel(check);
        const lastReading = formatLastReading(check);

        return (
          <tr key={i} className="hover:bg-grey-1 h-[59px] cursor-pointer" onClick={() => check.id && onRowClick(check.id)}>
            <td className="p-[10px]">
              <div className="d-flex align-items-center">
                <div>
                  <div className="text-sm/[21px] font-bold">
                    {check.asset ? (
                      <Link to={getWorkspaceRoute(`/assets/${check.asset.id}`)} onClick={(e) => e.stopPropagation()}>
                        {EntityRefUtil.createAssetLabel(check.asset)}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className="text-secondary-text text-xs/[18px] font-normal">{check.asset?.category?.name || 'N/A'}</div>
                </div>
              </div>
            </td>
            <td className="p-[10px] align-top">
              <div className="flex h-[45px] items-center gap-1 text-sm">
                {scheduleCheckLabel ? <Badge size="sm">{scheduleCheckLabel}</Badge> : <span className="text-secondary-text">-</span>}
              </div>
            </td>
            <td className="p-[10px] align-top">
              <div className="flex h-[45px] items-center text-sm">
                {lastReading ? <span>{lastReading}</span> : <span className="text-secondary-text">-</span>}
              </div>
            </td>
            <td className="p-[10px] align-middle">
              <div className="flex items-center gap-[10px]">
                <div className="w-[180px]">
                  <CheckProgressBar
                    isClosed={false}
                    isUnlinked={false}
                    dueProgress={dueProgress}
                    dateRemaining={check.dateRemaining}
                    showTooltip={true}
                    allUnitProgress={check.allUnitProgress}
                    allDateProgress={check.allDateProgress}
                  />
                </div>
                {status === 'overdue' ? <ExclamationIcon className="text-default-red shrink-0" size={20} /> : <div className="w-[20px]" />}
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}
